var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/*
 * Copyright (c) 2026 LS ELECTRIC Co. Ltd. All Reserved.
 */
/*******************************************************************
 *
 * @author 이훈희
 * @brief  MVD 전압 등급 관리 (sei_mvdlevel)
 *
 * 화면 구성은 「고효율 설비 List」와 같다.
 *   좌 flex-7 : 상단 필터(정격전압) → [행 추가] → 그리드 → 페이징
 *   우 flex-3 : 선택한 행의 입력/수정 항목, 그 아래 [취소][삭제][저장]
 *
 * 이 표는 기기 사양(sei_mvdmodel)이 정격전압으로 참조한다. 등급이 없는 전압군은
 * 모델 선정 STEP4(셀·레벨 산정)가 동작하지 않으므로, 참조 중인 등급은
 * 삭제와 정격전압 변경을 서버가 거부한다. 화면에도 「사용 기기」 수를 띄운다.
 *
 ********************************************************************/
import { useEffect, useState } from "react";
import EhpPagination from "@components/common/pagination/EhpPagination";
import CmmConfirmPop from "@components/common/popup/CmmConfirmPop";
import { alertState, confirmState } from "@recoil/assessmentState";
import { loadingBoxState } from "@recoil/menuState";
import { userInfoLoginState } from "@recoil/userState";
import * as HTTPUTIL from "@utils/api/HttpUtil";
import * as CUTIL from "@utils/commUtils";
import * as CONST from "@utils/Const";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
/** 입력/수정 항목. text 가 아니면 숫자만 받는다 */
const FIELDS = [
    { key: "ratedV", label: "정격전압 (V)", text: false, req: true },
    { key: "cellCnt", label: "상별 셀 수 N", text: false, req: true },
    { key: "phaseLevel", label: "상전압 레벨", text: false, req: false },
    { key: "lineLevel", label: "선간전압 레벨", text: false, req: false },
    { key: "inputPulse", label: "입력 펄스", text: false, req: false },
];
const EMPTY = FIELDS.reduce((acc, f) => (Object.assign(Object.assign({}, acc), { [f.key]: "" })), { id: null });
const PAGE_SIZE = 10;
const intOnly = (v) => String(v).replace(/[^0-9]/g, "");
const show = (v) => (v === null || v === undefined || v === "" ? "-" : v);
const toNum = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const toStr = (v) => (v === null || v === undefined ? "" : String(v));
export default function MvdLevelManagement() {
    const userInfo = useRecoilValue(userInfoLoginState);
    const [alerts, setAlerts] = useRecoilState(alertState);
    const [confirm, setConfirm] = useRecoilState(confirmState);
    const setRecoilIsLoadingBox = useSetRecoilState(loadingBoxState);
    const [levels, setLevels] = useState([]);
    const [fVolt, setFVolt] = useState("");
    const [pageInfo, setPageInfo] = useState({
        size: PAGE_SIZE,
        totalElements: 0,
        totalPages: 0,
        number: 0,
        psize: 10,
        listReload: false,
    });
    function handleCurPage(page) {
        setPageInfo(Object.assign(Object.assign({}, pageInfo), { number: page }));
    }
    const [selId, setSelId] = useState(null);
    const [form, setForm] = useState(Object.assign({}, EMPTY));
    useEffect(() => {
        LoadMaster();
    }, []);
    function ShowError(res, fallback) {
        setAlerts(Object.assign(Object.assign({}, alerts), { header: "전압 등급 관리", type: CONST.TYPE_ERROR, divTag: res && res.errorList ? res.errorList.map((err) => err.msg) : fallback }));
        CUTIL.jsopen_AlertPopup(CONST.POP_ALERT);
    }
    function LoadMaster() {
        return __awaiter(this, void 0, void 0, function* () {
            setRecoilIsLoadingBox(true);
            const res = yield HTTPUTIL.PromiseHttp({
                httpMethod: "GET",
                appPath: `/energyreport/inverter/mvd/master`,
                appQuery: {},
                userToken: userInfo.loginInfo.token,
            });
            setRecoilIsLoadingBox(false);
            if (!res)
                return;
            if (res.codeNum == CONST.API_200) {
                setLevels(res.body && res.body.levels ? res.body.levels : []);
            }
            else {
                ShowError(res, "전압 등급을 불러오지 못했습니다.");
            }
        });
    }
    /* ---------------- 필터 · 페이징 (화면 처리) ---------------- */
    const voltOptions = Array.from(new Set(levels.map((v) => v.ratedV).filter((v) => v !== null))).sort((a, b) => a - b);
    const filtered = levels.filter((v) => fVolt === "" || String(v.ratedV) === fVolt);
    useEffect(() => {
        const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
        setPageInfo((prev) => (Object.assign(Object.assign({}, prev), { size: PAGE_SIZE, totalElements: filtered.length, totalPages: totalPages, number: prev.number >= totalPages ? 0 : prev.number })));
    }, [levels, fVolt]);
    const pageRows = filtered.slice(pageInfo.number * PAGE_SIZE, (pageInfo.number + 1) * PAGE_SIZE);
    /* ---------------- 선택 · 저장 · 삭제 ---------------- */
    function SelectRow(row) {
        setSelId(row.id);
        setForm(FIELDS.reduce((acc, f) => (Object.assign(Object.assign({}, acc), { [f.key]: toStr(row[f.key]) })), { id: row.id }));
    }
    function NewRow() {
        setSelId("NEW");
        setForm(Object.assign({}, EMPTY));
    }
    function CancelRow() {
        setSelId(null);
        setForm(Object.assign({}, EMPTY));
    }
    function Save() {
        return __awaiter(this, void 0, void 0, function* () {
            const miss = FIELDS.filter((f) => f.req && String(form[f.key]).trim() === "");
            if (miss.length > 0) {
                setAlerts(Object.assign(Object.assign({}, alerts), { header: "전압 등급 관리", type: CONST.TYPE_ERROR, divTag: `${miss.map((f) => f.label).join(", ")} 은(는) 필수입니다.` }));
                CUTIL.jsopen_AlertPopup(CONST.POP_ALERT);
                return;
            }
            const payload = { id: form.id };
            FIELDS.forEach((f) => {
                payload[f.key] = f.text ? (form[f.key] === "" ? null : form[f.key]) : toNum(form[f.key]);
            });
            setRecoilIsLoadingBox(true);
            const res = yield HTTPUTIL.PromiseHttp({
                httpMethod: "POST",
                appPath: `/energyreport/inverter/mvd/level`,
                appQuery: payload,
                userToken: userInfo.loginInfo.token,
            });
            setRecoilIsLoadingBox(false);
            if (!res)
                return;
            if (res.codeNum == CONST.API_200) {
                setLevels(res.body && res.body.levels ? res.body.levels : []);
                CancelRow();
            }
            else {
                ShowError(res, "저장하지 못했습니다.");
            }
        });
    }
    function AskDelete() {
        const sel = levels.find((v) => v.id === form.id);
        if (sel && sel.usedCnt > 0) {
            setAlerts(Object.assign(Object.assign({}, alerts), { header: "전압 등급 관리", type: CONST.TYPE_ERROR, divTag: `이 전압을 쓰는 기기 사양이 ${sel.usedCnt}건 있어 삭제할 수 없습니다.` }));
            CUTIL.jsopen_AlertPopup(CONST.POP_ALERT);
            return;
        }
        setConfirm(Object.assign(Object.assign({}, confirm), { header: "전압 등급 삭제", content: `${form.ratedV ? `${form.ratedV} V` : "선택한 전압 등급"} 등급을 삭제하시겠습니까?`, leftBtnName: "취소", rightBtnName: "삭제", rightBtnCallBack: () => __awaiter(this, void 0, void 0, function* () {
                setRecoilIsLoadingBox(true);
                const res = yield HTTPUTIL.PromiseHttp({
                    httpMethod: "DELETE",
                    appPath: `/energyreport/inverter/mvd/level/${form.id}`,
                    appQuery: {},
                    userToken: userInfo.loginInfo.token,
                });
                setRecoilIsLoadingBox(false);
                if (!res)
                    return;
                if (res.codeNum == CONST.API_200) {
                    setLevels(res.body && res.body.levels ? res.body.levels : []);
                    CancelRow();
                }
                else {
                    ShowError(res, "삭제하지 못했습니다.");
                }
            }) }));
        CUTIL.jsopen_AlertPopup(CONST.POP_CMM_CONFIRM);
    }
    const editing = selId !== null;
    const selUsed = levels.find((v) => v.id === form.id);
    return (_jsxs(_Fragment, { children: [_jsxs("article", Object.assign({ className: "flex-7" }, { children: [_jsx("div", Object.assign({ className: "box__header flex-column brd-bottom-0 pb-0" }, { children: _jsx("div", Object.assign({ className: "flex-inline gap-24" }, { children: _jsxs("div", Object.assign({ className: "select mvdLvVolt", onClick: (e) => CUTIL.onClickSelect(e, CUTIL.selectOption) }, { children: [_jsxs("div", Object.assign({ className: "selected" }, { children: [_jsx("div", Object.assign({ className: "selected-value" }, { children: fVolt === "" ? "정격전압 전체" : `${fVolt} V` })), _jsx("div", { className: "arrow" })] })), _jsxs("ul", { children: [_jsx("li", Object.assign({ className: "option", onClick: (e) => setFVolt("") }, { children: "\uC815\uACA9\uC804\uC555 \uC804\uCCB4" })), voltOptions.map((v, idx) => (_jsx("li", Object.assign({ className: "option", onClick: (e) => setFVolt(String(v)) }, { children: `${v} V` }), `mvdLvVolt_${idx.toString()}`)))] })] })) })) })), _jsxs("div", Object.assign({ className: `box__body pt-0 ${filtered.length <= 0 ? "nodata" : ""}` }, { children: [_jsx("div", Object.assign({ className: "tbl__top mt-46" }, { children: _jsx("div", Object.assign({ className: "right" }, { children: _jsx("button", Object.assign({ type: "button", className: "btn-basic", onClick: (e) => NewRow() }, { children: _jsx("span", { children: "\uD589 \uCD94\uAC00" }) })) })) })), _jsxs("div", Object.assign({ className: "tbl-list tbl-select line-1" }, { children: [_jsx("p", Object.assign({ className: "nodata__txt" }, { children: "\uB370\uC774\uD130\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4." })), _jsxs("table", Object.assign({ summary: "\uC815\uACA9\uC804\uC555, \uC0C1\uBCC4 \uC140 \uC218, \uC0C1\uC804\uC555 \uB808\uBCA8, \uC120\uAC04\uC804\uC555 \uB808\uBCA8, \uC785\uB825 \uD384\uC2A4, \uC0AC\uC6A9 \uAE30\uAE30 \uC218\uB85C \uAD6C\uC131\uB41C \uC804\uC555 \uB4F1\uAE09 \uBAA9\uB85D\uC785\uB2C8\uB2E4." }, { children: [_jsx("caption", { children: "\uC804\uC555 \uB4F1\uAE09\uBCC4" }), _jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", Object.assign({ scope: "col" }, { children: "\uC815\uACA9\uC804\uC555(V)" })), _jsx("th", Object.assign({ scope: "col" }, { children: "\uC0C1\uBCC4 \uC140 \uC218" })), _jsx("th", Object.assign({ scope: "col" }, { children: "\uC0C1\uC804\uC555 \uB808\uBCA8" })), _jsx("th", Object.assign({ scope: "col" }, { children: "\uC120\uAC04\uC804\uC555 \uB808\uBCA8" })), _jsx("th", Object.assign({ scope: "col" }, { children: "\uC785\uB825 \uD384\uC2A4" })), _jsx("th", Object.assign({ scope: "col" }, { children: "\uC0AC\uC6A9 \uAE30\uAE30" }))] }) }), _jsx("tbody", { children: pageRows.map((row, idx) => (_jsxs("tr", Object.assign({ className: row.id === selId ? "selected" : "", onClick: (e) => SelectRow(row) }, { children: [_jsx("td", { children: show(row.ratedV) }), _jsx("td", { children: show(row.cellCnt) }), _jsx("td", { children: show(row.phaseLevel) }), _jsx("td", { children: show(row.lineLevel) }), _jsx("td", { children: show(row.inputPulse) }), _jsx("td", { children: row.usedCnt > 0 ? `${row.usedCnt}건` : "-" })] }), `mvdLevel_${idx.toString()}`))) })] }))] })), _jsx(EhpPagination, { componentName: "MVDLEVELLIST", pageInfo: pageInfo, handleCurPage: handleCurPage })] }))] })), _jsx("article", Object.assign({ className: "flex-3" }, { children: _jsxs("div", Object.assign({ className: `box__body p-40 ${editing ? "" : "nodata"}` }, { children: [_jsx("h3", { children: selId === "NEW" ? "전압 등급 등록" : "전압 등급 정보" }), _jsx("p", Object.assign({ className: "nodata__txt" }, { children: "\uC218\uC815\uD560 \uD589\uC744 \uC120\uD0DD\uD558\uAC70\uB098 [\uD589 \uCD94\uAC00] \uB97C \uB20C\uB7EC \uC8FC\uC138\uC694." })), editing && (_jsxs(_Fragment, { children: [_jsx("ul", Object.assign({ className: "form__input mt-24" }, { children: FIELDS.map((f) => (_jsxs("li", { children: [_jsx("p", Object.assign({ className: `tit ${f.req ? "star" : ""}` }, { children: f.label })), _jsx("div", Object.assign({ className: "input__area" }, { children: _jsx("input", { type: "text", value: form[f.key], onChange: (e) => setForm(Object.assign(Object.assign({}, form), { [f.key]: f.text ? e.target.value : intOnly(e.target.value) })), placeholder: f.text ? "입력하세요." : "숫자만" }) }))] }, `lf_${f.key}`))) })), selId !== "NEW" && selUsed && selUsed.usedCnt > 0 && (_jsxs("p", Object.assign({ className: "txt-info mvd-note" }, { children: ["\uC774 \uC804\uC555\uC744 \uC4F0\uB294 \uAE30\uAE30 \uC0AC\uC591\uC774 ", selUsed.usedCnt, "\uAC74 \uC788\uC5B4 \uC815\uACA9\uC804\uC555 \uBCC0\uACBD\uACFC \uC0AD\uC81C\uAC00 \uC81C\uD55C\uB429\uB2C8\uB2E4."] }))), _jsxs("div", Object.assign({ className: "btn-area mvd-btns" }, { children: [_jsx("button", Object.assign({ type: "button", className: "btn-basic bg-gray", onClick: (e) => CancelRow() }, { children: _jsx("span", { children: "\uCDE8\uC18C" }) })), selId !== "NEW" && (_jsx("button", Object.assign({ type: "button", className: "btn-basic bg-gray", onClick: (e) => AskDelete() }, { children: _jsx("span", { children: "\uC0AD\uC81C" }) }))), _jsx("button", Object.assign({ type: "button", className: "btn-basic", onClick: (e) => Save() }, { children: _jsx("span", { children: "\uC800\uC7A5" }) }))] }))] }))] })) })), _jsx(CmmConfirmPop, {})] }));
}
